(() => {
  const elementName = 'happysoy-savings-promotion';
  if (customElements.get(elementName)) return;

  const endpoint = '/apps/happysoy-bundles/api/mix/savings';
  const maxAgeMs = 60 * 1000;
  const requestTimeoutMs = 6000;
  const retryDelayMs = 1000;

  function savingsUrl(value) {
    const url = new URL(value || endpoint, location.origin);
    if (url.origin !== location.origin || url.pathname !== endpoint || url.search || url.hash || url.username || url.password) {
      throw new Error('Invalid savings URL');
    }
    return url.href;
  }

  function validSavings(value) {
    const now = Date.now();
    if (value?.scope !== 'configured-topics' || value.pending !== false || !Array.isArray(value.types)
      || !value.types.length || !Number.isFinite(value.checkedAt) || value.checkedAt <= 0
      || value.checkedAt > now + 1000 || now - value.checkedAt >= maxAgeMs) return null;
    const ids = new Set();
    let maxPercent = 0;
    for (const type of value.types) {
      if (!type || typeof type.id !== 'string' || !type.id || ids.has(type.id)
        || !Number.isSafeInteger(type.revision) || type.revision < 1
        || !Number.isSafeInteger(type.price) || type.price < 0) return null;
      ids.add(type.id);
      if (type.savings === null) continue;
      const saving = type.savings;
      if (!saving || ![saving.originalMin, saving.savingMin, saving.percentMin].every(Number.isSafeInteger)
        || saving.originalMin <= type.price || saving.savingMin !== saving.originalMin - type.price
        || saving.percentMin !== Math.floor(saving.savingMin * 100 / saving.originalMin)
        || saving.percentMin < 0 || saving.percentMin >= 100) return null;
      maxPercent = Math.max(maxPercent, saving.percentMin);
    }
    return maxPercent > 0 ? {maxPercent, checkedAt:value.checkedAt} : null;
  }

  customElements.define(elementName, class extends HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.active = true;
      if (this.fallbackText === undefined) this.fallbackText = this.textContent;
      this.onPageShow = event => { if (event.persisted) this.refresh(); };
      this.onVisibility = () => { if (document.visibilityState !== 'hidden' && (!this.checkedAt || Date.now() - this.checkedAt >= maxAgeMs)) this.refresh(); };
      document.addEventListener('visibilitychange', this.onVisibility);
      window.addEventListener('pageshow', this.onPageShow);
      this.refresh();
    }

    disconnectedCallback() {
      this.active = false;
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.cancelWork();
      this.textContent = this.fallbackText;
    }

    cancelWork() {
      clearTimeout(this.retryTimer);
      clearTimeout(this.expiryTimer);
      this.retryTimer = this.expiryTimer = null;
      this.request?.cancel();
      this.request = null;
      this.generation = null;
    }

    refresh() {
      this.cancelWork();
      this.checkedAt = null;
      this.textContent = this.fallbackText;
      if (!this.active) return;
      const generation = {};
      this.generation = generation;
      return this.loadSavings(generation, 0);
    }

    async loadSavings(generation, attempt) {
      if (!this.active || this.generation !== generation) return;
      const controller = new AbortController(), request = {};
      this.request = request;
      let timer;
      const deadline = new Promise((_, reject) => {
        request.cancel = () => {
          controller.abort();
          reject(new Error('Savings request cancelled'));
        };
        timer = setTimeout(request.cancel, requestTimeoutMs);
      });
      try {
        const url = savingsUrl(this.dataset.url);
        const template = this.dataset.template;
        if (!template || template.split('{percent}').length !== 2) throw new Error('Invalid savings text');
        const data = await Promise.race([
          fetch(url, { cache: 'no-store', credentials: 'same-origin', redirect: 'error', signal: controller.signal })
            .then(response => {
              if (!response.ok) throw new Error('Savings unavailable');
              return response.json();
            }),
          deadline,
        ]);
        if (!this.active || this.generation !== generation || this.request !== request) return;
        if (data?.pending === true) {
          if (attempt < 3) this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            this.loadSavings(generation, attempt + 1);
          }, retryDelayMs);
          return;
        }
        const savings = validSavings(data);
        if (!savings) return;
        this.checkedAt = savings.checkedAt;
        this.textContent = template.replace('{percent}', String(savings.maxPercent));
        // Refresh optional display data only while the page is in the foreground.
        this.expiryTimer = setTimeout(() => {
          this.expiryTimer = null;
          if (this.active && this.generation === generation) {
            this.textContent = this.fallbackText;
            this.checkedAt = null;
            if (document.visibilityState !== 'hidden') this.refresh();
          }
        }, Math.max(1000, savings.checkedAt + maxAgeMs - Date.now()));
      } catch {
        // The original theme text remains readable and its enclosing link usable.
      } finally {
        clearTimeout(timer);
        if (this.request === request) this.request = null;
      }
    }
  });
})();
